import { useState, useEffect, useMemo } from "react";
import api from "@/lib/api";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Badge } from "@/components/ui/badge";
import { Switch } from "@/components/ui/switch";
import { 
  Plus, Trash2, ChevronLeft, ChevronRight, Calendar, Tag, HelpCircle,
  TrendingUp, TrendingDown, Minus, AlertTriangle, BarChart3, PieChart as PieIcon,
  ArrowUpRight, ArrowDownRight, Lightbulb, Filter, Eye
} from "lucide-react";
import { toast } from "sonner";
import { 
  LineChart, Line, BarChart, Bar, XAxis, YAxis, CartesianGrid, 
  Tooltip, ResponsiveContainer, Legend, Cell, ReferenceDot,
  ComposedChart, Area
} from "recharts";
import { ExpensesSummaryCards, ExpensesCharts, ExpensesInsights, ExpensesList } from "@/components/expenses/ExpensesComponents";

const CATEGORY_INFO = {
  I: { label: "Indispensabili", color: "#3B82F6", bgColor: "bg-blue-500", lightBg: "bg-blue-50", textColor: "text-blue-700" },
  A: { label: "Accessorie", color: "#8B5CF6", bgColor: "bg-purple-500", lightBg: "bg-purple-50", textColor: "text-purple-700" },
  X: { label: "Imprevisti", color: "#EF4444", bgColor: "bg-red-500", lightBg: "bg-red-50", textColor: "text-red-700" }
};

const DEFAULT_SUBCATEGORIES = {
  I: ["Affitto/Mutuo", "Bollette", "Spesa alimentare", "Trasporti", "Salute/Farmaci", "Assicurazioni", "Telefono/Internet"],
  A: ["Abbigliamento", "Ristoranti/Bar", "Intrattenimento", "Sport/Palestra", "Viaggi", "Regali", "Cura personale", "Hobby"],
  X: ["Riparazioni casa", "Riparazioni auto", "Multe", "Spese mediche urgenti", "Emergenze", "Altro imprevisto"]
};

const PERIODS = {
  week: { label: "Settimana", days: 7 },
  month: { label: "Mese", days: 30 },
  quarter: { label: "3 Mesi", days: 90 }
};

const ExpensesPage = () => {
  const [expenses, setExpenses] = useState([]);
  const [previousExpenses, setPreviousExpenses] = useState([]);
  const [customSubcategories, setCustomSubcategories] = useState([]);
  const [loading, setLoading] = useState(true);
  
  // Period & filters
  const [period, setPeriod] = useState("month");
  const [periodOffset, setPeriodOffset] = useState(0);
  const [macroFilter, setMacroFilter] = useState("all");
  const [subcatFilter, setSubcatFilter] = useState("all");
  const [showPercentages, setShowPercentages] = useState(false);
  const [imprevistThreshold, setImprevistThreshold] = useState(100);
  
  // Dialogs
  const [addDialogOpen, setAddDialogOpen] = useState(false);
  const [subcatDialogOpen, setSubcatDialogOpen] = useState(false);
  const [helpDialogOpen, setHelpDialogOpen] = useState(false);
  const [showAllSubcats, setShowAllSubcats] = useState(false);
  
  // Form state
  const [formData, setFormData] = useState({
    description: "", amount: "", category: "I", subcategory: "", date: new Date().toISOString().split("T")[0]
  });
  const [saving, setSaving] = useState(false);
  const [newSubcat, setNewSubcat] = useState({ name: "", category: "I" });

  // Calculate date ranges
  const getDateRange = (periodType, offset = 0) => {
    const end = new Date();
    end.setDate(end.getDate() - (offset * PERIODS[periodType].days));
    const start = new Date(end);
    start.setDate(start.getDate() - PERIODS[periodType].days + 1);
    return {
      start: start.toISOString().split("T")[0],
      end: end.toISOString().split("T")[0]
    };
  };

  const currentRange = useMemo(() => getDateRange(period, periodOffset), [period, periodOffset]);
  const previousRange = useMemo(() => getDateRange(period, periodOffset + 1), [period, periodOffset]);

  useEffect(() => {
    fetchExpenses();
    fetchCustomSubcategories();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentRange, previousRange]);

  const fetchExpenses = async () => {
    setLoading(true);
    try {
      const [currentRes, prevRes] = await Promise.all([
        api.get('/expenses', { params: { start_date: currentRange.start, end_date: currentRange.end } }),
        api.get('/expenses', { params: { start_date: previousRange.start, end_date: previousRange.end } })
      ]);
      setExpenses(currentRes.data || []);
      setPreviousExpenses(prevRes.data || []);
    } catch (error) {
      if (process.env.NODE_ENV === 'development') console.error("Error fetching expenses:", error);
      toast.error("Errore nel caricamento");
    } finally {
      setLoading(false);
    }
  };

  const fetchCustomSubcategories = async () => {
    try {
      const response = await api.get('/expense-subcategories');
      setCustomSubcategories(response.data || []);
    } catch (error) {
      if (process.env.NODE_ENV === 'development') console.error("Error fetching subcategories:", error);
    }
  };

  const getSubcategoriesForCategory = (category) => {
    const defaults = DEFAULT_SUBCATEGORIES[category] || [];
    const custom = customSubcategories.filter(s => s.category === category).map(s => s.name);
    return [...new Set([...defaults, ...custom])];
  };

  // Filter expenses
  const filteredExpenses = useMemo(() => {
    return expenses.filter(e => {
      if (macroFilter !== "all" && e.category !== macroFilter) return false;
      if (subcatFilter !== "all" && e.subcategory !== subcatFilter) return false;
      return true;
    });
  }, [expenses, macroFilter, subcatFilter]);

  // Calculate totals
  const totals = useMemo(() => {
    const current = { total: 0, I: 0, A: 0, X: 0 };
    const previous = { total: 0, I: 0, A: 0, X: 0 };
    
    filteredExpenses.forEach(e => {
      current.total += e.amount;
      current[e.category] = (current[e.category] || 0) + e.amount;
    });
    
    previousExpenses.forEach(e => {
      if (macroFilter !== "all" && e.category !== macroFilter) return;
      if (subcatFilter !== "all" && e.subcategory !== subcatFilter) return;
      previous.total += e.amount;
      previous[e.category] = (previous[e.category] || 0) + e.amount;
    });
    
    return { current, previous };
  }, [filteredExpenses, previousExpenses, macroFilter, subcatFilter]);

  // Calculate delta
  const getDelta = (current, previous) => {
    if (previous === 0) return { value: current, percent: null, status: "new" };
    const diff = current - previous;
    const percent = ((diff / previous) * 100);
    return {
      value: diff,
      percent,
      status: diff > 0 ? "up" : diff < 0 ? "down" : "stable"
    };
  };

  // Group by day/week for charts
  const chartData = useMemo(() => {
    const aggregateBy = period === "quarter" ? "week" : "day";
    const dataMap = {};
    
    // Initialize all dates/weeks in range
    const start = new Date(currentRange.start);
    const end = new Date(currentRange.end);
    
    if (aggregateBy === "day") {
      for (let d = new Date(start); d <= end; d.setDate(d.getDate() + 1)) {
        const key = d.toISOString().split("T")[0];
        dataMap[key] = { date: key, total: 0, I: 0, A: 0, X: 0, expenses: [] };
      }
    } else {
      // Group by week
      let weekStart = new Date(start);
      while (weekStart <= end) {
        const key = weekStart.toISOString().split("T")[0];
        dataMap[key] = { date: key, total: 0, I: 0, A: 0, X: 0, expenses: [] };
        weekStart.setDate(weekStart.getDate() + 7);
      }
    }
    
    // Populate data
    filteredExpenses.forEach(e => {
      let key = e.date;
      if (aggregateBy === "week") {
        const d = new Date(e.date);
        const weekStart = new Date(d);
        weekStart.setDate(d.getDate() - d.getDay() + 1);
        key = Object.keys(dataMap).find(k => {
          const kDate = new Date(k);
          return d >= kDate && d < new Date(kDate.setDate(kDate.getDate() + 7));
        }) || Object.keys(dataMap)[0];
      }
      
      if (dataMap[key]) {
        dataMap[key].total += e.amount;
        dataMap[key][e.category] = (dataMap[key][e.category] || 0) + e.amount;
        dataMap[key].expenses.push(e);
      }
    });
    
    // Calculate moving average
    const data = Object.values(dataMap).sort((a, b) => a.date.localeCompare(b.date));
    const windowSize = aggregateBy === "day" ? 7 : 4;
    
    data.forEach((item, idx) => {
      const windowStart = Math.max(0, idx - windowSize + 1);
      const window = data.slice(windowStart, idx + 1);
      item.movingAvg = window.reduce((sum, w) => sum + w.total, 0) / window.length;
    });
    
    return data;
  }, [filteredExpenses, currentRange, period]);

  // Top subcategories
  const topSubcategories = useMemo(() => {
    const subcatMap = {};
    filteredExpenses.forEach(e => {
      const key = e.subcategory || "Altro";
      if (!subcatMap[key]) subcatMap[key] = { name: key, amount: 0, category: e.category };
      subcatMap[key].amount += e.amount;
    });
    return Object.values(subcatMap).sort((a, b) => b.amount - a.amount);
  }, [filteredExpenses]);

  // Big imprevisti
  const bigImprevisti = useMemo(() => {
    return expenses.filter(e => e.category === "X" && e.amount >= imprevistThreshold)
      .sort((a, b) => b.amount - a.amount);
  }, [expenses, imprevistThreshold]);

  // Insights
  const insights = useMemo(() => {
    const result = [];
    const totalDelta = getDelta(totals.current.total, totals.previous.total);
    
    if (totalDelta.status === "up") {
      result.push(`Questo periodo hai speso €${Math.abs(totalDelta.value).toFixed(0)} in più rispetto al precedente (+${totalDelta.percent?.toFixed(0)}%).`);
    } else if (totalDelta.status === "down") {
      result.push(`Questo periodo hai speso €${Math.abs(totalDelta.value).toFixed(0)} in meno rispetto al precedente (${totalDelta.percent?.toFixed(0)}%).`);
    } else if (totalDelta.status === "stable") {
      result.push(`La spesa è stabile rispetto al periodo precedente.`);
    }
    
    // Top category
    const topCat = Object.entries(totals.current).filter(([k]) => k !== "total").sort((a, b) => b[1] - a[1])[0];
    if (topCat && topCat[1] > 0) {
      result.push(`La categoria che pesa di più è: ${CATEGORY_INFO[topCat[0]]?.label} (€${topCat[1].toFixed(0)}).`);
    }
    
    // Top subcategory
    if (topSubcategories.length > 0) {
      result.push(`La sottocategoria principale è: ${topSubcategories[0].name} (€${topSubcategories[0].amount.toFixed(0)}).`);
    }
    
    // Imprevisti
    if (bigImprevisti.length > 0) {
      result.push(`Hai avuto ${bigImprevisti.length} spese impreviste sopra €${imprevistThreshold}.`);
    }
    
    return result;
  }, [totals, topSubcategories, bigImprevisti, imprevistThreshold]);

  // Handlers
  const handleAddExpense = async (e) => {
    e.preventDefault();
    if (!formData.amount || !formData.date) {
      toast.error("Inserisci almeno importo e data");
      return;
    }
    setSaving(true);
    try {
      await api.post('/expenses', {
        description: formData.description || formData.subcategory || "Spesa",
        amount: parseFloat(formData.amount),
        category: formData.category,
        subcategory: formData.subcategory || null,
        date: formData.date
      });
      toast.success("Spesa aggiunta!");
      setFormData({ description: "", amount: "", category: "I", subcategory: "", date: new Date().toISOString().split("T")[0] });
      setAddDialogOpen(false);
      fetchExpenses();
    } catch (error) {
      toast.error("Errore nel salvataggio");
    } finally {
      setSaving(false);
    }
  };

  const handleDeleteExpense = async (expenseId) => {
    try {
      await api.delete(`/expenses/${expenseId}`);
      toast.success("Spesa eliminata");
      fetchExpenses();
    } catch (error) {
      toast.error("Errore nell'eliminazione");
    }
  };

  const handleAddSubcategory = async (e) => {
    e.preventDefault();
    if (!newSubcat.name.trim()) return;
    try {
      await api.post('/expense-subcategories', { name: newSubcat.name.trim(), category: newSubcat.category });
      toast.success("Sottocategoria creata!");
      setNewSubcat({ name: "", category: "I" });
      fetchCustomSubcategories();
    } catch (error) {
      toast.error(error.response?.data?.detail === "Subcategory already exists" ? "Esiste già" : "Errore");
    }
  };

  const handleDeleteSubcategory = async (subcategoryId) => {
    try {
      await api.delete(`/expense-subcategories/${subcategoryId}`);
      toast.success("Eliminata");
      fetchCustomSubcategories();
    } catch (error) {
      toast.error("Errore");
    }
  };

  const formatPeriodLabel = () => {
    const start = new Date(currentRange.start);
    const end = new Date(currentRange.end);
    const opts = { day: "numeric", month: "short" };
    return `${start.toLocaleDateString("it-IT", opts)} - ${end.toLocaleDateString("it-IT", opts)}`;
  };

  const formatXAxis = (dateStr) => {
    const d = new Date(dateStr);
    if (period === "quarter") {
      return `S${Math.ceil(d.getDate() / 7)}`;
    }
    return d.toLocaleDateString("it-IT", { day: "numeric", month: "short" });
  };

  const CustomTooltip = ({ active, payload, label }) => {
    if (!active || !payload) return null;
    return (
      <div className="bg-white p-3 rounded-xl shadow-lg border text-sm">
        <p className="font-semibold mb-2">{new Date(label).toLocaleDateString("it-IT", { weekday: "short", day: "numeric", month: "short" })}</p>
        {payload.map((p) => (
          <p key={p.dataKey || p.name} style={{ color: p.color }} className="flex justify-between gap-4">
            <span>{p.name}:</span>
            <span className="font-mono">€{p.value?.toFixed(2)}</span>
          </p>
        ))}
      </div>
    );
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-indigo-600"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6 animate-fade-in pb-8 max-w-7xl mx-auto" data-testid="expenses-page">
      
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:justify-between sm:items-start gap-4">
        <div>
          <h1 className="text-2xl font-bold text-gray-800">Spese</h1>
          <p className="text-sm text-gray-500">Dashboard di monitoraggio</p>
        </div>
        <div className="flex gap-2 flex-wrap">
          <Button variant="outline" size="sm" onClick={() => setHelpDialogOpen(true)} className="rounded-xl">
            <HelpCircle className="w-4 h-4 mr-1" /> Guida
          </Button>
          <Button variant="outline" size="sm" onClick={() => setSubcatDialogOpen(true)} className="rounded-xl">
            <Tag className="w-4 h-4 mr-1" /> Categorie
          </Button>
          <Button size="sm" className="rounded-xl bg-indigo-600 hover:bg-indigo-700" onClick={() => setAddDialogOpen(true)} data-testid="add-expense-btn">
            <Plus className="w-4 h-4 mr-1" /> Aggiungi
          </Button>
        </div>
      </div>

      {/* Filters Bar */}
      <div className="soft-card p-4">
        <div className="flex flex-wrap items-center gap-4">
          {/* Period Selector */}
          <div className="flex items-center gap-2">
            <span className="text-sm text-gray-500">Periodo:</span>
            <div className="flex bg-gray-100 rounded-xl p-1">
              {Object.entries(PERIODS).map(([key, { label }]) => (
                <button
                  key={key}
                  onClick={() => { setPeriod(key); setPeriodOffset(0); }}
                  className={`px-3 py-1.5 text-sm rounded-lg transition-colors ${
                    period === key ? "bg-white shadow-sm text-gray-800" : "text-gray-500 hover:text-gray-700"
                  }`}
                >
                  {label}
                </button>
              ))}
            </div>
          </div>
          
          {/* Period Navigation */}
          <div className="flex items-center gap-2">
            <Button variant="ghost" size="sm" onClick={() => setPeriodOffset(p => p + 1)} className="rounded-xl">
              <ChevronLeft className="w-4 h-4" />
            </Button>
            <span className="text-sm font-medium min-w-[140px] text-center">{formatPeriodLabel()}</span>
            <Button variant="ghost" size="sm" onClick={() => setPeriodOffset(p => Math.max(0, p - 1))} disabled={periodOffset === 0} className="rounded-xl">
              <ChevronRight className="w-4 h-4" />
            </Button>
          </div>
          
          {/* Macro Filter */}
          <Select value={macroFilter} onValueChange={setMacroFilter}>
            <SelectTrigger className="w-40 rounded-xl">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">Tutte le macro</SelectItem>
              <SelectItem value="I">Solo Indispensabili</SelectItem>
              <SelectItem value="A">Solo Accessorie</SelectItem>
              <SelectItem value="X">Solo Imprevisti</SelectItem>
            </SelectContent>
          </Select>
          
          {/* Toggle % / € */}
          <div className="flex items-center gap-2 ml-auto">
            <span className="text-sm text-gray-500">€</span>
            <Switch checked={showPercentages} onCheckedChange={setShowPercentages} />
            <span className="text-sm text-gray-500">%</span>
          </div>
        </div>
      </div>

      {/* Quick Summary Cards */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
        {/* Total */}
        <div className="soft-card p-5">
          <p className="text-sm text-gray-500 mb-1">Totale Periodo</p>
          <p className="text-3xl font-bold text-gray-800">€{totals.current.total.toFixed(0)}</p>
          <DeltaBadge delta={getDelta(totals.current.total, totals.previous.total)} />
        </div>
        
        {/* By Category */}
        {Object.entries(CATEGORY_INFO).map(([key, info]) => (
          <div key={key} className="soft-card p-5">
            <div className="flex items-center gap-2 mb-1">
              <span className={`w-3 h-3 rounded-full ${info.bgColor}`} />
              <p className="text-sm text-gray-500">{info.label}</p>
            </div>
            <p className="text-2xl font-bold text-gray-800">€{(totals.current[key] || 0).toFixed(0)}</p>
            <DeltaBadge delta={getDelta(totals.current[key] || 0, totals.previous[key] || 0)} />
          </div>
        ))}
      </div>

      {/* Charts Grid */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        
        {/* Card A: Andamento nel tempo */}
        <div className="soft-card p-5 lg:col-span-2">
          <div className="flex items-center justify-between mb-4">
            <h3 className="font-semibold text-gray-700 flex items-center gap-2">
              <TrendingUp className="w-5 h-5 text-indigo-500" />
              Andamento Totale
            </h3>
          </div>
          <div className="h-64">
            <ResponsiveContainer width="100%" height="100%">
              <ComposedChart data={chartData}>
                <CartesianGrid strokeDasharray="3 3" stroke="#E5E7EB" />
                <XAxis dataKey="date" tickFormatter={formatXAxis} tick={{ fontSize: 11 }} />
                <YAxis tick={{ fontSize: 11 }} tickFormatter={(v) => `€${v}`} />
                <Tooltip content={<CustomTooltip />} />
                <Area type="monotone" dataKey="total" fill="#E0E7FF" stroke="#6366F1" strokeWidth={2} name="Totale" />
                <Line type="monotone" dataKey="movingAvg" stroke="#F59E0B" strokeWidth={2} strokeDasharray="5 5" dot={false} name="Media mobile" />
                {/* Mark big imprevisti */}
                {bigImprevisti.map((imp, idx) => {
                  const dataPoint = chartData.find(d => d.date === imp.date);
                  if (!dataPoint) return null;
                  return (
                    <ReferenceDot
                      key={`imprevisto-${imp.date}-${imp.amount}`}
                      x={imp.date}
                      y={dataPoint.total}
                      r={6}
                      fill="#EF4444"
                      stroke="#fff"
                      strokeWidth={2}
                    />
                  );
                })}
              </ComposedChart>
            </ResponsiveContainer>
          </div>
          {bigImprevisti.length > 0 && (
            <div className="mt-3 flex items-center gap-2 text-sm text-gray-500">
              <span className="w-3 h-3 rounded-full bg-red-500" />
              <span>Imprevisti &gt; €{imprevistThreshold}</span>
            </div>
          )}
        </div>

        {/* Card B: Macro per giorno (Stacked Bar) */}
        <div className="soft-card p-5">
          <div className="flex items-center justify-between mb-4">
            <h3 className="font-semibold text-gray-700 flex items-center gap-2">
              <BarChart3 className="w-5 h-5 text-purple-500" />
              Composizione Giornaliera
            </h3>
          </div>
          <div className="h-64">
            <ResponsiveContainer width="100%" height="100%">
              <BarChart data={chartData}>
                <CartesianGrid strokeDasharray="3 3" stroke="#E5E7EB" />
                <XAxis dataKey="date" tickFormatter={formatXAxis} tick={{ fontSize: 11 }} />
                <YAxis 
                  tick={{ fontSize: 11 }} 
                  tickFormatter={(v) => showPercentages ? `${v}%` : `€${v}`}
                  domain={showPercentages ? [0, 100] : ['auto', 'auto']}
                />
                <Tooltip content={<CustomTooltip />} />
                <Legend />
                <Bar 
                  dataKey={showPercentages ? (d) => d.total > 0 ? (d.I / d.total * 100) : 0 : "I"} 
                  stackId="a" fill={CATEGORY_INFO.I.color} name="Indispensabili" 
                />
                <Bar 
                  dataKey={showPercentages ? (d) => d.total > 0 ? (d.A / d.total * 100) : 0 : "A"} 
                  stackId="a" fill={CATEGORY_INFO.A.color} name="Accessorie" 
                />
                <Bar 
                  dataKey={showPercentages ? (d) => d.total > 0 ? (d.X / d.total * 100) : 0 : "X"} 
                  stackId="a" fill={CATEGORY_INFO.X.color} name="Imprevisti" 
                />
              </BarChart>
            </ResponsiveContainer>
          </div>
        </div>

        {/* Card C: Confronto con periodo precedente */}
        <div className="soft-card p-5">
          <div className="flex items-center justify-between mb-4">
            <h3 className="font-semibold text-gray-700 flex items-center gap-2">
              <ArrowUpRight className="w-5 h-5 text-green-500" />
              Confronto Periodo Precedente
            </h3>
          </div>
          
          {totals.previous.total > 0 ? (
            <div className="space-y-4">
              <div className="text-center py-4 bg-gray-50 rounded-xl">
                <p className="text-sm text-gray-500 mb-1">Variazione Totale</p>
                <DeltaDisplay delta={getDelta(totals.current.total, totals.previous.total)} large />
              </div>
              
              <div className="grid grid-cols-3 gap-3">
                {Object.entries(CATEGORY_INFO).map(([key, info]) => {
                  const delta = getDelta(totals.current[key] || 0, totals.previous[key] || 0);
                  return (
                    <div key={key} className={`p-3 rounded-xl ${info.lightBg}`}>
                      <p className={`text-xs ${info.textColor} mb-1`}>{info.label}</p>
                      <DeltaBadge delta={delta} showValue={false} />
                    </div>
                  );
                })}
              </div>
              
              <div className="text-xs text-gray-400 text-center">
                Confronto con: {new Date(previousRange.start).toLocaleDateString("it-IT", { day: "numeric", month: "short" })} - {new Date(previousRange.end).toLocaleDateString("it-IT", { day: "numeric", month: "short" })}
              </div>
            </div>
          ) : (
            <div className="text-center py-8 text-gray-400">
              <p className="text-sm">Nessun dato nel periodo precedente</p>
            </div>
          )}
        </div>

        {/* Card D: Top Sottocategorie */}
        <div className="soft-card p-5 lg:col-span-2">
          <div className="flex items-center justify-between mb-4">
            <h3 className="font-semibold text-gray-700 flex items-center gap-2">
              <PieIcon className="w-5 h-5 text-orange-500" />
              Top Sottocategorie
            </h3>
            {topSubcategories.length > 5 && (
              <Button variant="ghost" size="sm" onClick={() => setShowAllSubcats(!showAllSubcats)} className="rounded-xl text-sm">
                {showAllSubcats ? "Mostra meno" : `Mostra tutte (${topSubcategories.length})`}
              </Button>
            )}
          </div>
          
          {topSubcategories.length > 0 ? (
            <div className="space-y-3">
              {(showAllSubcats ? topSubcategories : topSubcategories.slice(0, 5)).map((subcat, idx) => {
                const percent = totals.current.total > 0 ? (subcat.amount / totals.current.total * 100) : 0;
                return (
                  <div key={subcat.name} className="flex items-center gap-3">
                    <span className="w-6 text-sm text-gray-400 text-right">{idx + 1}.</span>
                    <div className="flex-1">
                      <div className="flex justify-between items-center mb-1">
                        <span className="text-sm font-medium text-gray-700">{subcat.name}</span>
                        <span className="text-sm font-mono text-gray-800">€{subcat.amount.toFixed(0)}</span>
                      </div>
                      <div className="h-2 bg-gray-100 rounded-full overflow-hidden">
                        <div 
                          className="h-full rounded-full transition-all duration-500"
                          style={{ 
                            width: `${percent}%`,
                            backgroundColor: CATEGORY_INFO[subcat.category]?.color || '#6B7280'
                          }}
                        />
                      </div>
                    </div>
                    <span className="text-xs text-gray-400 w-12 text-right">{percent.toFixed(0)}%</span>
                  </div>
                );
              })}
            </div>
          ) : (
            <div className="text-center py-8 text-gray-400">
              <p className="text-sm">Nessuna spesa nel periodo</p>
            </div>
          )}
        </div>
      </div>

      {/* Imprevisti Grandi */}
      {bigImprevisti.length > 0 && (
        <div className="soft-card p-5 border-l-4 border-l-red-500">
          <div className="flex items-center gap-2 mb-4">
            <AlertTriangle className="w-5 h-5 text-red-500" />
            <h3 className="font-semibold text-gray-700">Imprevisti Rilevanti (&gt; €{imprevistThreshold})</h3>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
            {bigImprevisti.map(imp => (
              <div key={imp.expense_id} className="flex items-center justify-between p-3 bg-red-50 rounded-xl">
                <div>
                  <p className="text-sm font-medium text-gray-800">{imp.description || imp.subcategory || "Imprevisto"}</p>
                  <p className="text-xs text-gray-500">{imp.subcategory} • {new Date(imp.date).toLocaleDateString("it-IT", { day: "numeric", month: "short" })}</p>
                </div>
                <span className="text-lg font-bold text-red-600">€{imp.amount.toFixed(0)}</span>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Insights */}
      {insights.length > 0 && (
        <div className="soft-card p-5 bg-gradient-to-br from-amber-50 to-orange-50">
          <div className="flex items-center gap-2 mb-3">
            <Lightbulb className="w-5 h-5 text-amber-500" />
            <h3 className="font-semibold text-gray-700">Insight</h3>
          </div>
          <ul className="space-y-2">
            {insights.map((insight, idx) => (
              <li key={`insight-${idx}-${insight.substring(0,20)}`} className="text-sm text-gray-700 flex items-start gap-2">
                <span className="text-amber-500">•</span>
                {insight}
              </li>
            ))}
          </ul>
        </div>
      )}

      {/* Expenses List */}
      <div className="soft-card overflow-hidden">
        <div className="p-4 border-b border-gray-100 flex items-center justify-between">
          <h3 className="font-semibold text-gray-700">Movimenti ({filteredExpenses.length})</h3>
        </div>
        <div className="max-h-96 overflow-y-auto">
          {filteredExpenses.length === 0 ? (
            <div className="text-center py-12 text-gray-400">
              <p>Nessuna spesa nel periodo selezionato</p>
            </div>
          ) : (
            <div className="divide-y divide-gray-100">
              {filteredExpenses.sort((a, b) => b.date.localeCompare(a.date)).map(exp => (
                <div key={exp.expense_id} className="p-4 flex items-center gap-4 hover:bg-gray-50 group">
                  <span className={`w-8 h-8 rounded-xl flex items-center justify-center text-xs font-bold text-white ${CATEGORY_INFO[exp.category]?.bgColor}`}>
                    {exp.category}
                  </span>
                  <div className="flex-1 min-w-0">
                    <p className="text-sm font-medium text-gray-800 truncate">{exp.description || exp.subcategory || "Spesa"}</p>
                    <p className="text-xs text-gray-500">
                      {exp.subcategory && <span className="mr-2">{exp.subcategory}</span>}
                      {new Date(exp.date).toLocaleDateString("it-IT", { day: "numeric", month: "short" })}
                    </p>
                  </div>
                  <span className="text-lg font-mono font-semibold text-gray-800">€{exp.amount.toFixed(2)}</span>
                  <button
                    onClick={() => handleDeleteExpense(exp.expense_id)}
                    className="p-2 text-gray-400 hover:text-red-500 opacity-0 group-hover:opacity-100 transition-opacity"
                  >
                    <Trash2 className="w-4 h-4" />
                  </button>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>

      {/* ADD EXPENSE DIALOG */}
      <Dialog open={addDialogOpen} onOpenChange={setAddDialogOpen}>
        <DialogContent className="rounded-2xl sm:max-w-md">
          <DialogHeader>
            <DialogTitle>Aggiungi Spesa</DialogTitle>
          </DialogHeader>
          <form onSubmit={handleAddExpense} className="space-y-4">
            <div className="space-y-2">
              <Label>Importo (€) *</Label>
              <Input
                type="number" step="0.01" min="0" placeholder="Es. 45.50"
                value={formData.amount}
                onChange={(e) => setFormData({ ...formData, amount: e.target.value })}
                className="rounded-xl text-lg"
                autoFocus
              />
            </div>
            <div className="space-y-2">
              <Label>Data *</Label>
              <Input
                type="date" value={formData.date}
                onChange={(e) => setFormData({ ...formData, date: e.target.value })}
                className="rounded-xl"
              />
            </div>
            <div className="space-y-2">
              <Label>Macro-categoria *</Label>
              <div className="grid grid-cols-3 gap-2">
                {Object.entries(CATEGORY_INFO).map(([key, info]) => (
                  <button
                    key={key} type="button"
                    onClick={() => setFormData({ ...formData, category: key, subcategory: "" })}
                    className={`p-3 rounded-xl border-2 transition-all text-center ${
                      formData.category === key 
                        ? `${info.lightBg} border-current ${info.textColor}` 
                        : "border-gray-200 hover:border-gray-300"
                    }`}
                  >
                    <span className={`w-6 h-6 rounded-full ${info.bgColor} text-white mx-auto mb-1 flex items-center justify-center text-xs font-bold`}>{key}</span>
                    <span className="text-xs">{info.label}</span>
                  </button>
                ))}
              </div>
            </div>
            <div className="space-y-2">
              <Label>Sotto-categoria</Label>
              <Select value={formData.subcategory || "_none"} onValueChange={(v) => setFormData({ ...formData, subcategory: v === "_none" ? "" : v })}>
                <SelectTrigger className="rounded-xl"><SelectValue placeholder="Seleziona..." /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="_none">Nessuna</SelectItem>
                  {getSubcategoriesForCategory(formData.category).map(s => <SelectItem key={s} value={s}>{s}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-2">
              <Label>Descrizione</Label>
              <Input
                placeholder="Es. Spesa Conad"
                value={formData.description}
                onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                className="rounded-xl"
              />
            </div>
            <Button type="submit" className="w-full rounded-xl" disabled={saving}>
              {saving ? "Salvataggio..." : "Salva Spesa"}
            </Button>
          </form>
        </DialogContent>
      </Dialog>

      {/* MANAGE SUBCATEGORIES DIALOG */}
      <Dialog open={subcatDialogOpen} onOpenChange={setSubcatDialogOpen}>
        <DialogContent className="rounded-2xl max-w-lg max-h-[80vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Gestisci Sotto-categorie</DialogTitle>
          </DialogHeader>
          <form onSubmit={handleAddSubcategory} className="flex gap-2 mb-4">
            <Input placeholder="Nome..." value={newSubcat.name} onChange={(e) => setNewSubcat({ ...newSubcat, name: e.target.value })} className="rounded-xl flex-1" />
            <Select value={newSubcat.category} onValueChange={(v) => setNewSubcat({ ...newSubcat, category: v })}>
              <SelectTrigger className="rounded-xl w-20"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="I">I</SelectItem>
                <SelectItem value="A">A</SelectItem>
                <SelectItem value="X">X</SelectItem>
              </SelectContent>
            </Select>
            <Button type="submit" size="sm" className="rounded-xl"><Plus className="w-4 h-4" /></Button>
          </form>
          <div className="space-y-4">
            {["I", "A", "X"].map(cat => (
              <div key={cat}>
                <div className="flex items-center gap-2 mb-2">
                  <span className={`w-5 h-5 rounded-full ${CATEGORY_INFO[cat].bgColor} text-white flex items-center justify-center text-xs font-bold`}>{cat}</span>
                  <span className="text-sm font-semibold">{CATEGORY_INFO[cat].label}</span>
                </div>
                <div className="flex flex-wrap gap-2">
                  {DEFAULT_SUBCATEGORIES[cat].map(s => (
                    <span key={s} className="px-2 py-1 bg-gray-100 rounded-lg text-xs text-gray-500">{s}</span>
                  ))}
                  {customSubcategories.filter(s => s.category === cat).map(s => (
                    <div key={s.subcategory_id} className="flex items-center gap-1 px-2 py-1 bg-indigo-100 rounded-lg text-xs text-indigo-700 group">
                      {s.name}
                      <button onClick={() => handleDeleteSubcategory(s.subcategory_id)} className="opacity-0 group-hover:opacity-100 text-red-500">
                        <Trash2 className="w-3 h-3" />
                      </button>
                    </div>
                  ))}
                </div>
              </div>
            ))}
          </div>
        </DialogContent>
      </Dialog>

      {/* HELP DIALOG */}
      <Dialog open={helpDialogOpen} onOpenChange={setHelpDialogOpen}>
        <DialogContent className="rounded-2xl max-w-lg">
          <DialogHeader>
            <DialogTitle>Guida alle Spese</DialogTitle>
          </DialogHeader>
          <div className="space-y-4 text-sm">
            <div>
              <h4 className="font-semibold mb-2">Macro-categorie</h4>
              <ul className="space-y-2">
                {Object.entries(CATEGORY_INFO).map(([key, info]) => (
                  <li key={key} className="flex items-start gap-2">
                    <span className={`w-5 h-5 rounded-full ${info.bgColor} text-white flex items-center justify-center text-xs font-bold shrink-0`}>{key}</span>
                    <div>
                      <span className="font-medium">{info.label}</span>
                      <p className="text-gray-500 text-xs">
                        {key === "I" && "Spese necessarie: affitto, bollette, cibo"}
                        {key === "A" && "Spese non essenziali: ristoranti, hobby"}
                        {key === "X" && "Spese impreviste: riparazioni, emergenze"}
                      </p>
                    </div>
                  </li>
                ))}
              </ul>
            </div>
            <div className="bg-amber-50 p-3 rounded-xl">
              <h4 className="font-semibold mb-1 flex items-center gap-1"><Lightbulb className="w-4 h-4 text-amber-500" /> Suggerimento</h4>
              <p className="text-gray-600 text-xs">Usa le sotto-categorie per analisi più dettagliate. Gli imprevisti sopra €100 vengono evidenziati automaticamente.</p>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
};

// Helper Components
const DeltaBadge = ({ delta, showValue = true }) => {
  if (delta.status === "new" || delta.percent === null) return null;
  
  const isUp = delta.status === "up";
  const isDown = delta.status === "down";
  
  return (
    <span className={`inline-flex items-center gap-1 text-xs font-medium mt-1 ${
      isUp ? "text-red-600" : isDown ? "text-green-600" : "text-gray-500"
    }`}>
      {isUp && <ArrowUpRight className="w-3 h-3" />}
      {isDown && <ArrowDownRight className="w-3 h-3" />}
      {!isUp && !isDown && <Minus className="w-3 h-3" />}
      {delta.percent !== null && `${delta.percent > 0 ? "+" : ""}${delta.percent.toFixed(0)}%`}
      {showValue && delta.value !== 0 && <span className="text-gray-400 ml-1">(€{Math.abs(delta.value).toFixed(0)})</span>}
    </span>
  );
};

const DeltaDisplay = ({ delta, large = false }) => {
  if (delta.status === "new" || delta.percent === null) {
    return <p className="text-gray-400 text-sm">Nessun dato precedente</p>;
  }
  
  const isUp = delta.status === "up";
  const isDown = delta.status === "down";
  
  return (
    <div className={`flex items-center justify-center gap-2 ${large ? "text-2xl" : "text-lg"}`}>
      <span className={`font-bold ${isUp ? "text-red-600" : isDown ? "text-green-600" : "text-gray-600"}`}>
        {isUp && "+"}€{Math.abs(delta.value).toFixed(0)}
      </span>
      <span className={`text-sm px-2 py-0.5 rounded-full ${
        isUp ? "bg-red-100 text-red-600" : isDown ? "bg-green-100 text-green-600" : "bg-gray-100 text-gray-600"
      }`}>
        {delta.percent > 0 ? "+" : ""}{delta.percent.toFixed(0)}%
      </span>
    </div>
  );
};

export default ExpensesPage;
